import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Copy, Send, Loader2 } from "lucide-react";
import UrgencyBanner from "@/components/landing/UrgencyBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", phone: "", business_type: "", message: "" };

export default function LeadFormSection() {
  const [values, setValues] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [response, setResponse] = useState(null);
  const [copied, setCopied] = useState(false);

  const onChange = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const missing = Object.entries(values).filter(([, v]) => !v.trim()).map(([k]) => k);
    if (missing.length) {
      toast.error("Please fill all fields before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, values, { timeout: 25000 });
      setResponse(data);
      toast.success("Request received. Check your message below.");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Submission failed: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  const copyMessage = async () => {
    if (!response?.auto_response) return;
    try {
      await navigator.clipboard.writeText(response.auto_response);
      setCopied(true);
      toast.success("Message copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const reset = () => {
    setValues(INITIAL);
    setResponse(null);
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4" data-testid="form-kicker">Get Started</p>
          <h2 className="font-display text-4xl sm:text-5xl font-medium text-white leading-tight" data-testid="form-heading">
            Tell us about your business.
          </h2>
          <p className="mt-5 text-white/60 text-lg">We'll respond with your custom automation plan within 24 hours.</p>
          <div className="mt-6 max-w-xl">
            <UrgencyBanner />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="glass-card p-8 md:p-10 space-y-5"
            data-testid="lead-form"
          >
            <Field label="Name" name="name" value={values.name} onChange={onChange} testid="lead-input-name" />
            <Field label="Email" name="email" type="email" value={values.email} onChange={onChange} testid="lead-input-email" />
            <Field label="Phone" name="phone" value={values.phone} onChange={onChange} testid="lead-input-phone" />
            <Field label="Business Type" name="business_type" value={values.business_type} onChange={onChange} placeholder="e.g. Gym, Consultant, Education" testid="lead-input-business" />
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Message</label>
              <textarea
                name="message"
                rows={4}
                value={values.message}
                onChange={onChange}
                className="input-field resize-none"
                placeholder="What's slowing you down right now?"
                data-testid="lead-input-message"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              data-testid="lead-submit"
            >
              {submitting ? <><Loader2 className="animate-spin" size={18} /> Sending...</> : <><Send size={18} /> Get Your Automation Setup</>}
            </button>
            <p className="text-xs text-white/40 text-center">
              We reply from <span className="text-white/70">hi@vyomex.org</span> — usually within 24 hours.
            </p>
          </motion.form>

          <div className="space-y-6">
            <AnimatePresence mode="wait">
              {response ? (
                <motion.div
                  key="response"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="glass-card overflow-hidden glow-cyan"
                  data-testid="auto-response-box"
                >
                  <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-black/40 font-mono text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-emerald-300">Auto-response delivered</span>
                    </div>
                    <span className="text-white/40">vyomex://response</span>
                  </div>
                  <div className="p-6 md:p-8">
                    <pre className="whitespace-pre-wrap font-mono text-sm text-white/90 leading-relaxed" data-testid="auto-response-text">
{response.auto_response}
                    </pre>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <button
                        onClick={copyMessage}
                        className="btn-ghost inline-flex items-center gap-2"
                        data-testid="copy-response-btn"
                      >
                        {copied ? <><Check size={16} className="text-emerald-400" /> Copied</> : <><Copy size={16} /> Copy Message</>}
                      </button>
                      <button onClick={reset} className="btn-ghost" data-testid="reset-form-btn">
                        Send another
                      </button>
                    </div>
                    <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 gap-3 text-xs">
                      <StatusPill label="Email → Owner" ok={response.email_sent} testid="status-email" />
                      <StatusPill label="Google Sheets" ok={response.sheet_saved} testid="status-sheets" />
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="glass-card p-8 md:p-10 h-full"
                  data-testid="auto-response-placeholder"
                >
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">Auto-response preview</p>
                  <div className="space-y-2 text-white/50 font-mono text-sm">
                    <div className="h-3 w-3/4 rounded bg-white/10" />
                    <div className="h-3 w-2/3 rounded bg-white/10" />
                    <div className="h-3 w-5/6 rounded bg-white/10" />
                    <div className="h-3 w-1/2 rounded bg-white/10" />
                  </div>
                  <p className="text-white/50 text-sm mt-8 leading-relaxed">
                    After you submit the form, an on-brand auto-response appears here —
                    plus confirmation that the email & Sheets sync fired.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder || label}
        className="input-field"
        data-testid={testid}
      />
    </div>
  );
}

function StatusPill({ label, ok, testid }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/40 px-3 py-2" data-testid={testid}>
      <span className="text-white/60">{label}</span>
      <span className={`inline-flex items-center gap-1 ${ok ? "text-emerald-400" : "text-amber-300"}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-amber-400"} animate-pulse`} />
        {ok ? "Sent" : "Queued"}
      </span>
    </div>
  );
}
